import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { getAllFabricOptions, createFabricOption, updateFabricOption, getAllBrands, getAllRequirements } from '../../store/actions';
import Header from '../../components/layout/Header';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import { FABRIC_DECISION, SWATCH_STATUS, formatDate, formatINR, label, today } from '../../constants/garmentCodes';
import useCreateIntent, { CREATE_INTENT } from '../../customHooks/useCreateIntent';
import { uploadFileToMinio } from '../../customHooks/S3Upload';
import toast from 'react-hot-toast';

const DECISION = { DRAFT: 1, SUBMITTED: 2, UNDER_REVIEW: 3, APPROVED: 4, REJECTED: 5 };
const EMPTY_FORM = {
  name: '',
  code: '',
  material: '',
  composition: '',
  gsm: '',
  width: '',
  finish: '',
  texture: '',
  stretch: '',
  colour: '',
  colourCode: '#ffffff',
  pricePerUnit: '',
  uom: 'per metre',
  moq: '',
  availableQty: '',
  leadTime: '',
  supplier: '',
  image: '',
};
const inputCls = 'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400';

function Swatch({ fabric, className }) {
  if (fabric.image) return <img src={fabric.image} alt={fabric.name} className={`${className} object-cover`} />;
  return <div className={className} style={{ background: fabric.colourCode || '#f3f4f6' }} />;
}

function FabricCard({ fabric, onClick }) {
  const decision = label(FABRIC_DECISION, fabric.decisionStatus);
  return (
    <button onClick={onClick} className={`bg-white rounded-lg border border-gray-100 overflow-hidden hover:shadow-md transition-shadow text-left group ${fabric.__optimistic ? 'opacity-60' : ''}`}>
      <div className="aspect-square bg-gray-50 overflow-hidden relative">
        <Swatch fabric={fabric} className="w-full h-full group-hover:scale-105 transition-transform duration-300" />
        <div className="absolute top-2 left-2"><Badge status={decision} /></div>
      </div>
      <div className="p-3">
        <p className="text-[13px] font-semibold text-gray-900 mb-0.5">{fabric.name}</p>
        <p className="text-[11px] text-gray-400 mb-2">{fabric.code || '—'} · {fabric.brandName || '—'}</p>
        <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[11px]">
          <span className="text-gray-400">GSM</span><span className="text-gray-700 text-right font-mono">{fabric.gsm ?? '—'}</span>
          <span className="text-gray-400">Price</span><span className="text-gray-700 text-right font-mono">{formatINR(fabric.pricePerUnit)}</span>
          <span className="text-gray-400">MOQ</span><span className="text-gray-700 text-right font-mono">{fabric.moq ?? '—'}</span>
          <span className="text-gray-400">Lead</span><span className="text-gray-700 text-right">{fabric.leadTime || '—'}</span>
        </div>
      </div>
    </button>
  );
}

function FabricDetailDrawer({ fabric, onClose, onSubmitToBrand }) {
  if (!fabric) return null;
  const decision = label(FABRIC_DECISION, fabric.decisionStatus);
  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-[420px] bg-white shadow-2xl overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-[14px] font-semibold text-gray-900">{fabric.name}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        <div className="p-6 space-y-5">
          <Swatch fabric={fabric} className="w-full aspect-video rounded-lg" />
          <div className="flex items-center gap-2 flex-wrap">
            <Badge status={decision} />
            {fabric.physicalSwatchStatus && <Badge status={label(SWATCH_STATUS, fabric.physicalSwatchStatus)} />}
          </div>
          <div className="grid grid-cols-2 gap-3 text-[12px]">
            {[
              ['Brand', fabric.brandName],
              ['Requirement', fabric.requirementName],
              ['Material', fabric.material],
              ['Composition', fabric.composition],
              ['Colour', fabric.colour],
              ['GSM', fabric.gsm],
              ['Width', fabric.width],
              ['Finish', fabric.finish],
              ['Price', fabric.pricePerUnit != null ? `${formatINR(fabric.pricePerUnit)} ${fabric.uom || ''}` : null],
              ['MOQ', fabric.moq],
              ['Available Qty', fabric.availableQty],
              ['Lead Time', fabric.leadTime],
              ['Supplier', fabric.supplier],
              ['Submitted', fabric.submittedDate ? formatDate(fabric.submittedDate) : null],
            ].map(([k, v]) => (
              <div key={k}>
                <p className="text-gray-400">{k}</p>
                <p className="font-medium text-gray-800">{v ?? '—'}</p>
              </div>
            ))}
          </div>

          {fabric.rejectionReason && (
            <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-[12px] text-red-700">Rejected: {fabric.rejectionReason}</div>
          )}
          {fabric.approvalNote && (
            <div className="p-3 bg-green-50 border border-green-100 rounded-lg text-[12px] text-green-700">Approval note: {fabric.approvalNote}</div>
          )}
          {fabric.decisionStatus === DECISION.DRAFT && (
            <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg text-[12px] text-blue-700">This option is in draft. Submit it to share with the brand.</div>
          )}

          {(fabric.decisionStatus === DECISION.DRAFT || fabric.decisionStatus === 6) && (
            <button onClick={() => onSubmitToBrand(fabric)} className="w-full px-4 py-2.5 text-[12px] font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55]">
              Submit to Brand
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Fabrics() {
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const requirementId = params.get('requirementId') || '';
  const [view, setView] = useState('gallery');
  const [brandFilter, setBrandFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedRequirementId, setSelectedRequirementId] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  useCreateIntent(CREATE_INTENT.FABRIC, () => setAddOpen(true));

  const fabrics = useSelector((state) => state.fabricoption.options);
  const loading = useSelector((state) => state.fabricoption.loading);
  const brands = useSelector((state) => state.brand.brands);
  const requirements = useSelector((state) => state.requirement.requirements);

  useEffect(() => {
    dispatch(getAllBrands());
    dispatch(getAllRequirements());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAllFabricOptions({ brandId: brandFilter, decisionStatus: '', requirementId }));
  }, [dispatch, brandFilter, requirementId]);

  const stats = fabrics.reduce(
    (acc, f) => {
      acc.total += 1;
      if (f.sourcingSetId != null) acc.sets.add(f.sourcingSetId);
      if (f.decisionStatus === DECISION.APPROVED) acc.approved += 1;
      if (f.decisionStatus === DECISION.SUBMITTED || f.decisionStatus === DECISION.UNDER_REVIEW) acc.pending += 1;
      return acc;
    },
    { total: 0, approved: 0, pending: 0, sets: new Set() },
  );

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const num = (v) => (v === '' ? null : Number(v));

  const submitToBrand = (fabric) => {
    dispatch(updateFabricOption({ payload: { id: fabric.id, decisionStatus: DECISION.SUBMITTED, submittedDate: today() } }));
    setSelected(null);
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingImage(true);
    try {
      const url = await uploadFileToMinio(file, 'fabrics');
      if (url) setForm((f) => ({ ...f, image: url }));
    } catch (err) {
      console.error('Fabric image upload failed:', err);
      toast.error('Image upload failed. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  // The backend can only ever list a fabric option by walking Requirement -> Engagement ->
  // company (there's no companyId on FabricOption itself), so one created without a
  // requirementId is saved successfully but becomes permanently unlistable - it silently
  // vanishes. Every option must be linked to a requirement: pre-filled when the page was opened
  // from one (?requirementId=...), otherwise picked here.
  const effectiveRequirementId = requirementId || selectedRequirementId;

  const create = (e) => {
    e.preventDefault();
    if (uploadingImage || !effectiveRequirementId) return;
    dispatch(
      createFabricOption({
        payload: {
          requirementId: Number(effectiveRequirementId),
          name: form.name.trim(),
          code: form.code || null,
          material: form.material || null,
          composition: form.composition || null,
          gsm: num(form.gsm),
          width: form.width || null,
          finish: form.finish || null,
          texture: form.texture || null,
          stretch: form.stretch || null,
          colour: form.colour || null,
          colourCode: form.colourCode,
          pricePerUnit: num(form.pricePerUnit),
          uom: form.uom || null,
          moq: num(form.moq),
          availableQty: num(form.availableQty),
          leadTime: form.leadTime || null,
          supplier: form.supplier || null,
          image: form.image || null,
          decisionStatus: DECISION.DRAFT,
          physicalSwatchStatus: 1,
        },
      }),
    );
    setForm(EMPTY_FORM);
    setSelectedRequirementId('');
    setAddOpen(false);
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <Header title="Fabrics" breadcrumbs={[{ workspace: true }, { label: 'Fabrics' }]} />
      <main className="flex-1 overflow-auto p-6 pb-20 md:pb-6">
        <div className="grid grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-lg border border-gray-100 p-4">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Sourcing Sets</p>
            <p className="text-[22px] font-bold text-gray-900">{stats.sets.size}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-100 p-4">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Total Options</p>
            <p className="text-[22px] font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-green-50 rounded-lg border border-green-100 p-4">
            <p className="text-[10px] text-green-600 uppercase tracking-wide mb-1">Approved by Brand</p>
            <p className="text-[22px] font-bold text-green-700">{stats.approved}</p>
          </div>
          <div className="bg-amber-50 rounded-lg border border-amber-100 p-4">
            <p className="text-[10px] text-amber-600 uppercase tracking-wide mb-1">Awaiting Decision</p>
            <p className="text-[22px] font-bold text-amber-700">{stats.pending}</p>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} className="text-[12px] border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none">
            <option value="">All brands</option>
            {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          {loading && <span className="text-[11px] text-gray-400">Loading…</span>}
          <div className="flex gap-1 ml-auto border border-gray-200 rounded-lg overflow-hidden">
            <button onClick={() => setView('gallery')} className={`px-3 py-1.5 text-[12px] ${view === 'gallery' ? 'bg-gray-100 text-gray-800' : 'text-gray-400 hover:bg-gray-50'}`}>Gallery</button>
            <button onClick={() => setView('table')} className={`px-3 py-1.5 text-[12px] ${view === 'table' ? 'bg-gray-100 text-gray-800' : 'text-gray-400 hover:bg-gray-50'}`}>Table</button>
          </div>
          <button onClick={() => setAddOpen(true)} className="px-3 py-1.5 text-[12px] font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55]">
            + Add Option
          </button>
        </div>

        {!loading && fabrics.length === 0 && <div className="bg-white rounded-lg border border-gray-100 py-12 text-center text-[12px] text-gray-400">No fabric options</div>}

        {view === 'gallery' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {fabrics.map((f) => <FabricCard key={f.id} fabric={f} onClick={() => !f.__optimistic && setSelected(f)} />)}
          </div>
        ) : (
          fabrics.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left">Fabric</th>
                      <th className="px-4 py-3 text-left">Brand</th>
                      <th className="px-4 py-3 text-left">Requirement</th>
                      <th className="px-4 py-3 text-left">Material</th>
                      <th className="px-4 py-3 text-right">GSM</th>
                      <th className="px-4 py-3 text-right">Price</th>
                      <th className="px-4 py-3 text-right">MOQ</th>
                      <th className="px-4 py-3 text-left">Lead Time</th>
                      <th className="px-4 py-3 text-left">Decision</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {fabrics.map((f) => (
                      <tr key={f.id} className={`hover:bg-gray-50 cursor-pointer ${f.__optimistic ? 'opacity-60' : ''}`} onClick={() => !f.__optimistic && setSelected(f)}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <Swatch fabric={f} className="w-8 h-8 rounded" />
                            <div>
                              <p className="text-[12px] font-medium text-gray-800">{f.name}</p>
                              <p className="text-[10px] text-gray-400 font-mono">{f.code || '—'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-[12px] text-gray-700">{f.brandName || '—'}</td>
                        <td className="px-4 py-3 text-[12px] text-gray-700">{f.requirementName || '—'}</td>
                        <td className="px-4 py-3 text-[12px] text-gray-700">{f.material || '—'}</td>
                        <td className="px-4 py-3 text-right font-mono text-[12px] text-gray-700">{f.gsm ?? '—'}</td>
                        <td className="px-4 py-3 text-right font-mono text-[12px] text-gray-700">{formatINR(f.pricePerUnit)}</td>
                        <td className="px-4 py-3 text-right font-mono text-[12px] text-gray-700">{f.moq ?? '—'}</td>
                        <td className="px-4 py-3 text-[12px] text-gray-700">{f.leadTime || '—'}</td>
                        <td className="px-4 py-3"><Badge status={label(FABRIC_DECISION, f.decisionStatus)} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        )}
      </main>

      <FabricDetailDrawer fabric={selected} onClose={() => setSelected(null)} onSubmitToBrand={submitToBrand} />

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Add Fabric Option" size="lg">
        <form onSubmit={create} className="space-y-4">
          <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
            {form.image ? (
              <img src={form.image} alt="Fabric" className="mx-auto mb-3 h-32 w-32 rounded-lg object-cover" />
            ) : (
              <>
                <p className="text-[13px] font-medium text-gray-700 mb-1">Upload Fabric Image</p>
                <p className="text-[12px] text-gray-400">Fabric shot · Swatch close-up · Drape shot</p>
              </>
            )}
            <label className={`mt-3 inline-block px-4 py-2 text-[12px] text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 ${uploadingImage ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}>
              {uploadingImage ? 'Uploading…' : form.image ? 'Change Image' : 'Browse Files'}
              <input type="file" accept="image/*" className="hidden" disabled={uploadingImage} onChange={handleImageChange} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-[11px] font-medium text-gray-600 mb-1">Requirement *</label>
              {requirementId ? (
                <input disabled value={requirements.find((r) => String(r.id) === String(requirementId))?.name || `#${requirementId}`} className={`${inputCls} bg-gray-50`} />
              ) : (
                <select required value={selectedRequirementId} onChange={(e) => setSelectedRequirementId(e.target.value)} className={inputCls}>
                  <option value="">Select requirement</option>
                  {requirements.map((r) => (
                    <option key={r.id} value={r.id}>{r.code || r.name} {r.brandName ? `· ${r.brandName}` : ''}</option>
                  ))}
                </select>
              )}
            </div>
            <div><label className="block text-[11px] font-medium text-gray-600 mb-1">Fabric Name *</label><input required placeholder="e.g. Cotton Poplin - White" value={form.name} onChange={set('name')} className={inputCls} /></div>
            <div><label className="block text-[11px] font-medium text-gray-600 mb-1">Option Code</label><input placeholder="e.g. CP-WHT-001" value={form.code} onChange={set('code')} className={`${inputCls} font-mono`} /></div>
            <div><label className="block text-[11px] font-medium text-gray-600 mb-1">Material *</label><input required placeholder="e.g. Cotton" value={form.material} onChange={set('material')} className={inputCls} /></div>
            <div><label className="block text-[11px] font-medium text-gray-600 mb-1">GSM *</label><input required type="number" placeholder="110" value={form.gsm} onChange={set('gsm')} className={`${inputCls} font-mono`} /></div>
            <div><label className="block text-[11px] font-medium text-gray-600 mb-1">Price per metre *</label><input required type="number" placeholder="185" value={form.pricePerUnit} onChange={set('pricePerUnit')} className={`${inputCls} font-mono`} /></div>
            <div><label className="block text-[11px] font-medium text-gray-600 mb-1">MOQ (metres) *</label><input required type="number" placeholder="100" value={form.moq} onChange={set('moq')} className={`${inputCls} font-mono`} /></div>
            <div><label className="block text-[11px] font-medium text-gray-600 mb-1">Supplier *</label><input required placeholder="e.g. Arvind Mills" value={form.supplier} onChange={set('supplier')} className={inputCls} /></div>
            <div><label className="block text-[11px] font-medium text-gray-600 mb-1">Lead Time *</label><input required placeholder="e.g. 14 days" value={form.leadTime} onChange={set('leadTime')} className={inputCls} /></div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setAddOpen(false)} className="flex-1 px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={uploadingImage || !effectiveRequirementId} className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55] disabled:opacity-60">Save Option</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
